import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Spin, Tag, Anchor, Button } from 'antd'
import { LeftOutlined, CalendarOutlined } from '@ant-design/icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import 'highlight.js/styles/github-dark.css'

const CaseDetail = () => {
  const { caseId } = useParams()
  const { t, i18n } = useTranslation()
  const [caseData, setCaseData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [relatedCases, setRelatedCases] = useState([])
  const [headings, setHeadings] = useState([])

  useEffect(() => {
    // 滚动到顶部
    window.scrollTo(0, 0)

    // 获取案例详情，传递语言参数
    axios.get(`/api/cases/${caseId}`, { params: { lang: i18n.language } })
      .then(response => {
        setCaseData(response.data)
        
        // 提取标题用于目录
        const content = response.data.content
        const headingRegex = /^#{2,3}\s+(.+)$/gm
        const matches = []
        let match
        while ((match = headingRegex.exec(content)) !== null) {
          matches.push({
            level: match[0].split('#').length - 1,
            text: match[1].trim(),
            id: match[1].trim().toLowerCase().replace(/\s+/g, '-'),
          })
        }
        setHeadings(matches)

        // 获取相关案例
        return axios.get(`/api/cases?category=${response.data.category}&lang=${i18n.language}`)
      })
      .then(response => {
        const related = response.data
          .filter(c => c.id !== caseId)
          .slice(0, 3)
        setRelatedCases(related)
      })
      .catch(error => {
        console.error('Failed to fetch case:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [caseId, i18n.language])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spin size="large" tip={t('cases.loading')} />
      </div>
    )
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('cases.notFound')}</h2>
          <Link to="/cases">
            <Button type="primary">{t('cases.backToList')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 返回按钮 */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-4">
          <Link to="/cases">
            <Button icon={<LeftOutlined />}>{t('cases.backToList')}</Button>
          </Link>
        </div>
      </div>

      {/* 头部信息 */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 py-16 pt-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-wrap gap-2 mb-4">
              <Tag color="cyan">{caseData.category}</Tag>
              {caseData.tags && caseData.tags.map(tag => (
                <Tag key={tag} color="blue">{tag}</Tag>
              ))}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {caseData.title}
            </h1>
            <p className="text-xl text-white/90 mb-4">{caseData.summary}</p>
            <div className="flex items-center text-white/80">
              <CalendarOutlined className="mr-2" />
              <span>{caseData.date}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 主要内容 */}
      <section className="py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* 左侧内容 */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-lg p-8 md:p-12"
              >
                <div className="markdown-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeHighlight, rehypeRaw]}
                    components={{
                      h2: ({node, ...props}) => <h2 id={props.children[0].toLowerCase().replace(/\s+/g, '-')} {...props} />,
                      h3: ({node, ...props}) => <h3 id={props.children[0].toLowerCase().replace(/\s+/g, '-')} {...props} />,
                    }}
                  >
                    {caseData.content}
                  </ReactMarkdown>
                </div>
              </motion.div>
            </div>

            {/* 右侧边栏 */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* 目录导航 */}
                {headings.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-xl shadow-lg p-6"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{t('common.readMore')}</h3>
                    <Anchor
                      offsetTop={100}
                      items={headings.map(h => ({
                        key: h.id,
                        href: `#${h.id}`,
                        title: h.text,
                      }))}
                    />
                  </motion.div>
                )}

                {/* 相关案例 */}
                {relatedCases.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl shadow-lg p-6"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{t('cases.relatedCases')}</h3>
                    <div className="space-y-4">
                      {relatedCases.map(relatedCase => (
                        <Link
                          key={relatedCase.id}
                          to={`/cases/${relatedCase.id}`}
                          className="block group"
                        >
                          <div className="border border-gray-200 rounded-lg p-3 hover:border-primary-500 transition-colors">
                            <h4 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-1 line-clamp-2">
                              {relatedCase.title}
                            </h4>
                            <p className="text-sm text-gray-500 line-clamp-2">
                              {relatedCase.summary}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-white/90 mb-8">
            {t('cta.subtitle')}
          </p>
          <Link to="/contact">
            <Button type="primary" size="large" className="bg-white text-primary-600 border-0 hover:bg-gray-100">
              {t('nav.consultation')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}

export default CaseDetail

